/* Shared input styles — CSS module equivalent of TextField.styles.tsx exports */

/* Base input styles (sharedInputStyles) */
.sharedInput {
  appearance: none;
  background: transparent;
  border: none;
  caret-color: var(--colors-brand-primary);
  color: var(--colors-text-primary);
  flex: 1;
  outline: none;
  padding: 0 var(--spaces-8);
  width: 100%;
  margin: 0;
  -webkit-text-size-adjust: none;

  &::placeholder {
    color: var(--colors-text-tertiary);
  }
}

/* Size variants (textFieldSizes) — use data-size="small"|"medium"|"large" */
.inputSize {
  &:where([data-size='small']) {
    font-family: var(--fonts-body);
    font-weight: var(--font-weights-regular);
    font-size: var(--font-sizes-60);
    line-height: 14px;
    padding: 5px var(--spaces-8);
  }

  &:where([data-size='medium']) {
    font-family: var(--fonts-body);
    font-weight: var(--font-weights-regular);
    font-size: var(--font-sizes-70);
    line-height: var(--spaces-16);
    padding: var(--spaces-8);
  }

  &:where([data-size='large']) {
    font-family: var(--fonts-body);
    font-weight: var(--font-weights-regular);
    font-size: var(--font-sizes-70);
    line-height: var(--spaces-24);
    padding: var(--spaces-8) var(--spaces-12);
  }
}

/* Prevent iOS zoom on touch devices — use --vapor-input-font-size-min when custom min needed */
.preventTouchZoom {
  @media screen and (pointer: coarse) and (max-width: 768px) {
    font-size: max(16px, var(--vapor-input-font-size-min, 16px));
  }
}

/* Focus ring (activeInputBorder) */
.activeInputBorder {
  box-shadow:
    inset 0 0 0 var(--spaces-1) var(--colors-border-interactive-selected),
    0 0 0 var(--spaces-1) var(--colors-border-interactive-selected);
}

/* Hover border (hoverInputBorder) */
.hoverInputBorder {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-hover);
}

/* Error state (errorColorsCSS) */
.errorColors {
  background: var(--colors-background-interactive-error);
  color: var(--colors-text-tertiary);
  transition:
    box-shadow 0.25s var(--transitions-eases-primary),
    color 0.25s var(--transitions-eases-primary);
}

.errorColors:where(:hover),
.errorColors:where(.hover) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-error);
}

.errorColors:where(:focus-within) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-opaque-error);
  color: var(--colors-text-body);
}

/* Input container (sharedInputContainerStyles) — use data-has-error, data-muted, data-disabled */
.inputContainer {
  border-radius: var(--radii-4);
  color: var(--colors-text-tertiary);
  transition:
    box-shadow 0.25s var(--transitions-eases-primary),
    color 0.25s var(--transitions-eases-primary);
}

/* Box-shadow border chrome — compose with inputContainer on wrappers that need a framed field */
.inputContainerBorder:where(:not([data-has-error])) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-secondary);
}

.inputContainerBorder:where(:not([data-has-error])):where(:not([data-muted])):where(:not(:focus-within)):where(:hover),
.inputContainerBorder:where(:not([data-has-error])):where(:not([data-muted])):where(:not(:focus-within)):where(.hover) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-hover);
}

.inputContainerBorder:where(:not([data-has-error])):where(:not([data-muted])):where(:focus-within) {
  color: var(--colors-text-body);
  box-shadow:
    inset 0 0 0 var(--spaces-1) var(--colors-border-interactive-selected),
    0 0 0 var(--spaces-1) var(--colors-border-interactive-selected);
}

.inputContainerBorder:where([data-has-error]) {
  background: var(--colors-background-interactive-error);
}

.inputContainerBorder:where([data-has-error]):where(:hover),
.inputContainerBorder:where([data-has-error]):where(.hover) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-error);
}

.inputContainerBorder:where([data-has-error]):where(:focus-within) {
  box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-opaque-error);
  color: var(--colors-text-body);
}

/* Disabled state */
.inputContainer:where([data-disabled]) {
  pointer-events: none;
  opacity: 0.3;
}

/* Safari autofill for inputs inside container */
.inputContainer input::-webkit-credentials-auto-fill-button {
  background-color: var(--colors-icon-secondary);
}

.inputContainer input::-webkit-contacts-auto-fill-button {
  background-color: var(--colors-icon-secondary) !important;
}

.inputContainer input:not(:-webkit-autofill)::-webkit-contacts-auto-fill-button {
  background-color: var(--colors-icon-secondary) !important;
}

.inputContainer input:not(:-webkit-autofill:hover)::-webkit-contacts-auto-fill-button {
  background-color: var(--colors-icon-interactive-hover);
}

.inputContainer input:not(:-webkit-autofill:focus)::-webkit-contacts-auto-fill-button {
  background-color: var(--colors-icon-interactive-activated);
}

.inputContainer input:not(:-webkit-autofill:active)::-webkit-contacts-auto-fill-button {
  background-color: var(--colors-icon-interactive-activated);
}

/* Opt-in background — use alongside inputContainer when a filled background is needed */
.inputContainerBackground:where(:not([data-has-error])) {
  background: var(--colors-components-input-background);
}

/* Input container root (InputContainer) — layout; combine with inputContainer */
.inputContainerRoot {
  align-items: center;
  display: flex;
  padding: 0;
  position: relative;
}

/* Raw input element (StyledInput equivalent) — sharedInput + inputSize + preventTouchZoom + type resets */
.input {
  appearance: none;
  background: transparent;
  border: none;
  caret-color: var(--colors-brand-primary);
  color: var(--colors-text-primary);
  flex: 1;
  outline: none;
  width: 100%;
  margin: 0;
  -webkit-text-size-adjust: none;

  &::placeholder {
    color: var(--colors-text-tertiary);
  }

  @media screen and (pointer: coarse) and (max-width: 768px) {
    font-size: max(16px, var(--vapor-input-font-size-min, 16px));
  }

  &[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  &[type='number'] {
    font-variant-numeric: tabular-nums;
  }
}
